import type { OutlineData } from '~/design-system/components/OutlinePanel';
import type { PlanningSection } from '~/types/overview';

export const isNewChapterFormat = (content: string | null | undefined): boolean => {
  if (!content) {
    return false;
  }
  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed?.chapters);
  } catch {
    return false;
  }
};

export const parseOutlineData = (content: string | null | undefined): OutlineData | null => {
  if (!content) {
    return null;
  }
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed?.chapters)) {
      const converted = {
        ...parsed,
        chapters: parsed.chapters.map((chapter: { canvases?: Array<{ content?: unknown }> }) => ({
          ...chapter,
          canvases: chapter.canvases?.map((canvas: { content?: unknown }) => ({
            ...canvas,
            content: convertCanvasContentToMarkdown(canvas.content),
          })) || [],
        })),
      };
      return converted as OutlineData;
    }
  } catch {}
  return null;
};

export interface TiptapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface TiptapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  text?: string;
  marks?: TiptapMark[];
}

export interface TiptapDoc {
  type: 'doc';
  content: TiptapNode[];
}

const addDiffToNodes = (nodes: TiptapNode[], diffType: 'added' | 'removed'): TiptapNode[] => {
  return nodes.map(node => {
    const newNode = { ...node };
    
    if (node.type === 'paragraph' || node.type === 'heading' || node.type === 'listItem') {
      newNode.attrs = { ...node.attrs, diffType };
    }
    
    if (node.content) {
      newNode.content = addDiffToNodes(node.content, diffType);
    }
    
    return newNode;
  });
};

const getNodeText = (node: TiptapNode): string => {
  if (node.text) return node.text;
  if (node.content) return node.content.map(getNodeText).join('');
  return '';
};

const getNodeSignature = (node: TiptapNode): string => {
  return `${node.type}:${getNodeText(node)}`;
};

export const computeDiffContent = (
  currentNodes: TiptapNode[],
  pendingNodes: TiptapNode[]
): TiptapNode[] => {
  if (currentNodes.length === 0 && pendingNodes.length === 0) {
    return [{ type: 'paragraph', content: [{ type: 'text', text: 'Start planning your story goal...' }] }];
  }

  if (currentNodes.length === 0) {
    return addDiffToNodes(pendingNodes, 'added');
  }

  const currentSignatures = new Set(currentNodes.map(getNodeSignature));
  const pendingSignatures = new Set(pendingNodes.map(getNodeSignature));
  
  const result: TiptapNode[] = [];
  const maxLength = Math.max(currentNodes.length, pendingNodes.length);

  for (let i = 0; i < maxLength; i++) {
    const currentNode = currentNodes[i];
    const pendingNode = pendingNodes[i];

    if (currentNode && !pendingSignatures.has(getNodeSignature(currentNode))) {
      result.push(...addDiffToNodes([currentNode], 'removed'));
    }

    if (pendingNode) {
      if (currentSignatures.has(getNodeSignature(pendingNode))) {
        result.push(pendingNode);
      } else {
        result.push(...addDiffToNodes([pendingNode], 'added'));
      }
    }
  }

  return result;
};

export const parseTiptapJson = (jsonString: string | null): TiptapDoc | null => {
  if (!jsonString) return null;
  try {
    const parsed = JSON.parse(jsonString);
    if (parsed && parsed.type === 'doc') {
      return parsed as TiptapDoc;
    }
    return null;
  } catch {
    return null;
  }
};

export const isMarkdownContent = (content: string | null | undefined): boolean => {
  if (!content) return false;
  const trimmed = content.trimStart();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(content);
      return !(parsed && parsed.type === 'doc');
    } catch {
      return true;
    }
  }
  return true;
};

/**
 * The markdown features a target editor's ProseMirror schema can actually hold.
 *
 * The default — every flag off — is the historical, deliberately NARROW dialect: headings, flat
 * bullet/ordered lists, `**bold**` and `*italic*`, nothing else. Two callers run restricted schemas
 * and MUST keep it: tldraw's rich text (canvasLayouts.ts) and the OutlinePanel diff editor (which
 * disables blockquote / codeBlock / horizontalRule). A node type their schema doesn't know makes
 * `setContent` throw the whole document away, so widening is strictly opt-in via RICH_MARKDOWN.
 */
export interface MarkdownFeatures {
  /** `[text](href)`, `<https://…>` and bare URLs → a `link` mark. */
  link?: boolean;
  /** `![alt](src)` → an inline `image` node (see ProseImage in the editor's proseExtensions). */
  image?: boolean;
  /** `` `x` `` → a `code` mark. */
  code?: boolean;
  /** `~~x~~` → a `strike` mark. */
  strike?: boolean;
  /** `_x_` / `__x__` as emphasis. Off by default so `snake_case` prose can't turn italic. */
  underscoreEmphasis?: boolean;
  /** Undo turndown's backslash escaping (`\[`, `\*`, `\#`…) instead of printing it literally. */
  escapes?: boolean;
  /** `- [ ]` / `- [x]` → taskList / taskItem. */
  taskList?: boolean;
  /** `> quoted` → blockquote, parsed recursively. */
  blockquote?: boolean;
  /** Fenced ``` / ~~~ blocks → codeBlock. */
  codeBlock?: boolean;
  /** `---` / `***` / `___` → horizontalRule. */
  horizontalRule?: boolean;
  /** Indented list items → a nested list inside the parent listItem. */
  nestedLists?: boolean;
  /** `Title\n=====` → heading. Hand-written .md files use it; our serializer never emits it. */
  setextHeadings?: boolean;
}

/** Everything the document / note editors' schema can hold (StarterKit + TaskList + ProseImage). */
export const RICH_MARKDOWN: MarkdownFeatures = {
  link: true,
  image: true,
  code: true,
  strike: true,
  underscoreEmphasis: true,
  escapes: true,
  taskList: true,
  blockquote: true,
  codeBlock: true,
  horizontalRule: true,
  nestedLists: true,
  setextHeadings: true,
};

// ── Inline ──────────────────────────────────────────────────────────────────────────────────────
//
// Backslash escapes are neutralised BEFORE any delimiter rule runs, by swapping `\*` for a single
// private-use codepoint. Doing it with a lookbehind instead would leave the `*` in the string, where
// the emphasis rules would still see it; swapping the character out removes the delimiter entirely.
const PUA_BASE = 0xe100;
const encodeEscapes = (s: string): string =>
  s.replace(/\\([\\`*_{}[\]()#+\-.!>~|])/g, (_m, c: string) => String.fromCharCode(PUA_BASE + c.charCodeAt(0)));
const decodeEscapes = (s: string): string =>
  s.replace(/[\uE100-\uE1FF]/g, (c) => String.fromCharCode(c.charCodeAt(0) - PUA_BASE));

const addMark = (marks: TiptapMark[] | undefined, mark: TiptapMark): TiptapMark[] => [...(marks ?? []), mark];

const pushText = (out: TiptapNode[], raw: string, marks?: TiptapMark[]): void => {
  const text = decodeEscapes(raw);
  // An empty text node is INVALID in ProseMirror and makes setContent fall back to an empty doc.
  if (!text) return;
  out.push(marks?.length ? { type: 'text', text, marks } : { type: 'text', text });
};

interface InlineRule {
  re: RegExp;
  on: (f: MarkdownFeatures) => boolean;
  build: (m: RegExpExecArray, f: MarkdownFeatures, marks?: TiptapMark[]) => TiptapNode[];
}

const wrap = (type: string): InlineRule['build'] => (m, f, marks) => parseInline(m[1], f, addMark(marks, { type }));

// Order is priority: on a tie at the same index the FIRST rule wins. None may be global — `exec` on a
// /g regex is stateful and these are re-run against every remaining slice.
const INLINE_RULES: InlineRule[] = [
  // Code first: a code span's content is literal, so no later rule may re-parse what's inside it.
  {
    re: /`([^`\n]+)`/,
    on: (f) => !!f.code,
    build: (m, _f, marks) => [{ type: 'text', text: decodeEscapes(m[1]), marks: addMark(marks, { type: 'code' }) }],
  },
  // Image before link — `![a](b)` also matches the link pattern one character in.
  {
    re: /!\[([^\]]*)\]\(\s*<?([^)\s>]*)>?(?:\s+"([^"]*)")?\s*\)/,
    on: (f) => !!f.image,
    build: (m, _f, marks) => {
      if (!m[2]) return [];
      const node: TiptapNode = {
        type: 'image',
        attrs: { src: decodeEscapes(m[2]), alt: decodeEscapes(m[1]) || null, title: m[3] || null },
      };
      // An image is an inline node, so it carries marks like text does — that's what keeps the
      // linked-badge shape `[![alt](img)](href)` a real link around a real image.
      if (marks?.length) node.marks = marks;
      return [node];
    },
  },
  {
    // The label allows ONE level of nesting (`[![alt](img)](href)`); a flat `[^\]]*` would stop at
    // the image's own `]` and split the badge into a broken half-link plus literal brackets.
    re: /\[((?:[^[\]]|\[[^\]]*\])*)\]\(\s*<?([^)\s>]*)>?(?:\s+"([^"]*)")?\s*\)/,
    on: (f) => !!f.link,
    build: (m, f, marks) => {
      // A label-less anchor (`[](#how-it-works)` — every heading permalink on a scraped page) is not
      // a link a reader wants; keeping it would print the raw `[](…)` brackets as text.
      if (!m[1].trim()) return [];
      const href = decodeEscapes(m[2]);
      return href ? parseInline(m[1], f, addMark(marks, { type: 'link', attrs: { href } })) : parseInline(m[1], f, marks);
    },
  },
  { re: /\*\*([\s\S]+?)\*\*/, on: () => true, build: wrap('bold') },
  { re: /__([\s\S]+?)__/, on: (f) => !!f.underscoreEmphasis, build: wrap('bold') },
  { re: /~~([\s\S]+?)~~/, on: (f) => !!f.strike, build: wrap('strike') },
  { re: /\*([^*\n]+?)\*/, on: () => true, build: wrap('italic') },
  { re: /_([^_\n]+?)_/, on: (f) => !!f.underscoreEmphasis, build: wrap('italic') },
  // Autolinks last: a URL already inside `[…](…)` was consumed by the link rule above.
  {
    re: /<(https?:\/\/[^\s>]+)>|(https?:\/\/[^\s<>()[\]]+)/,
    on: (f) => !!f.link,
    build: (m, _f, marks) => {
      const href = m[1] ?? m[2];
      return [{ type: 'text', text: href, marks: addMark(marks, { type: 'link', attrs: { href } }) }];
    },
  },
];

const parseInline = (text: string, f: MarkdownFeatures, marks?: TiptapMark[]): TiptapNode[] => {
  const out: TiptapNode[] = [];
  let rest = text;

  while (rest) {
    let best: { at: number; m: RegExpExecArray; rule: InlineRule } | null = null;
    for (const rule of INLINE_RULES) {
      if (!rule.on(f)) continue;
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.at)) best = { at: m.index, m, rule };
      if (best?.at === 0) break;
    }
    if (!best) {
      pushText(out, rest, marks);
      break;
    }
    if (best.at > 0) pushText(out, rest.slice(0, best.at), marks);
    out.push(...best.rule.build(best.m, f, marks));
    rest = rest.slice(best.at + best.m[0].length);
  }

  return out;
};

/** Inline entry point — escapes are encoded once here, then decoded on every emitted string. */
const parseInlineTop = (text: string, f: MarkdownFeatures): TiptapNode[] =>
  parseInline(f.escapes ? encodeEscapes(text) : text, f);

/** A paragraph/heading body, omitting `content` entirely when it parsed to nothing. */
const inlineBlock = (type: string, text: string, f: MarkdownFeatures, attrs?: Record<string, unknown>): TiptapNode => {
  const content = parseInlineTop(text, f);
  const node: TiptapNode = attrs ? { type, attrs } : { type };
  if (content.length > 0) node.content = content;
  return node;
};

/** Strip markdown syntax from a single line, leaving just its readable text. */
export const markdownInlineToText = (line: string): string =>
  parseInlineTop(line, RICH_MARKDOWN)
    .map((n) => n.text ?? '')
    .join('');

// ── Blocks ──────────────────────────────────────────────────────────────────────────────────────

// Allow an EMPTY heading (`# ` / `#` with no text) so it round-trips as an empty heading node rather
// than falling through to a paragraph showing the literal `#` (a doc with a body but a blank title).
const HEADING_RE = /^(#{1,6})(?:\s+(.*))?$/;
const SETEXT_RE = /^\s{0,3}(=+|-+)\s*$/;
const FENCE_RE = /^\s{0,3}(`{3,}|~{3,})\s*([^\s`]*)\s*$/;
const HR_RE = /^\s{0,3}(?:(?:\*[ \t]*){3,}|(?:-[ \t]*){3,}|(?:_[ \t]*){3,})$/;
const QUOTE_RE = /^\s{0,3}>\s?(.*)$/;
const LIST_RE = /^([ \t]*)(?:([-*+])|(\d+)[.)])[ \t]+(.*)$/;
const TASK_RE = /^\[([ xX])\][ \t]*(.*)$/;

const indentWidth = (ws: string): number => ws.replace(/\t/g, '    ').length;

/**
 * One list block starting at `start`: sibling items at the same indent and of the same flavour
 * (bullet / ordered / task), with deeper-indented items recursing into a nested list inside the item
 * they sit under. Appends the list node to `out` and returns the index of the first line after it.
 */
const parseList = (lines: string[], start: number, out: TiptapNode[], f: MarkdownFeatures): number => {
  const first = LIST_RE.exec(lines[start]) as RegExpExecArray;
  const baseIndent = indentWidth(first[1]);
  const ordered = !!first[3];
  const task = !!f.taskList && TASK_RE.test(first[4]);
  const items: TiptapNode[] = [];
  let i = start;

  while (i < lines.length) {
    if (lines[i].trim() === '') {
      // A blank line only continues the list when the next line is still an item of it (loose list).
      const after = i + 1 < lines.length ? LIST_RE.exec(lines[i + 1]) : null;
      if (!after || indentWidth(after[1]) < baseIndent) break;
      i++;
      continue;
    }

    const m = LIST_RE.exec(lines[i]);
    if (!m || indentWidth(m[1]) !== baseIndent || !!m[3] !== ordered) break;
    const boxed = !!f.taskList && TASK_RE.test(m[4]);
    if (boxed !== task) break;

    const box = boxed ? (TASK_RE.exec(m[4]) as RegExpExecArray) : null;
    const itemContent: TiptapNode[] = [inlineBlock('paragraph', box ? box[2] : m[4], f)];
    i++;

    // Deeper-indented list lines directly under this item become a nested list inside it.
    while (f.nestedLists && i < lines.length) {
      const child = LIST_RE.exec(lines[i]);
      if (!child || indentWidth(child[1]) <= baseIndent) break;
      i = parseList(lines, i, itemContent, f);
    }

    items.push(
      box
        ? { type: 'taskItem', attrs: { checked: box[1].toLowerCase() === 'x' }, content: itemContent }
        : { type: 'listItem', content: itemContent },
    );
  }

  out.push({ type: task ? 'taskList' : ordered ? 'orderedList' : 'bulletList', content: items });
  return i;
};

const parseBlocks = (lines: string[], f: MarkdownFeatures): TiptapNode[] => {
  const nodes: TiptapNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === '') {
      i++;
      continue;
    }

    // Fenced code first — everything up to the closing fence is literal, including `#` and `-`.
    const fence = f.codeBlock ? FENCE_RE.exec(line) : null;
    if (fence) {
      const closeRe = new RegExp(`^\\s{0,3}${fence[1][0] === '`' ? '`' : '~'}{${fence[1].length},}\\s*$`);
      const body: string[] = [];
      i++;
      while (i < lines.length && !closeRe.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      i++; // consume the closing fence (harmlessly overruns when the fence is unterminated)
      const text = body.join('\n');
      const node: TiptapNode = { type: 'codeBlock', attrs: { language: fence[2] || null } };
      if (text) node.content = [{ type: 'text', text }];
      nodes.push(node);
      continue;
    }

    if (f.horizontalRule && HR_RE.test(line)) {
      nodes.push({ type: 'horizontalRule' });
      i++;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      nodes.push(inlineBlock('heading', (heading[2] ?? '').trim(), f, { level: heading[1].length }));
      i++;
      continue;
    }

    if (f.blockquote && QUOTE_RE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && lines[i].trim() !== '') {
        const q = QUOTE_RE.exec(lines[i]);
        if (!q) break; // a non-`>` line ends the quote — no lazy continuation
        inner.push(q[1]);
        i++;
      }
      const content = parseBlocks(inner, f);
      nodes.push({ type: 'blockquote', content: content.length > 0 ? content : [{ type: 'paragraph' }] });
      continue;
    }

    // Indented items only start a list when the target schema can nest them; otherwise they stay
    // paragraphs, exactly as the narrow dialect has always rendered them.
    const list = LIST_RE.exec(line);
    if (list && (f.nestedLists || indentWidth(list[1]) === 0)) {
      i = parseList(lines, i, nodes, f);
      continue;
    }

    // Setext heading — a text line UNDERLINED by `===` / `---`. Checked from the TEXT line, so a
    // `---` that follows a blank line still reads as a horizontal rule.
    const under = f.setextHeadings && i + 1 < lines.length ? SETEXT_RE.exec(lines[i + 1]) : null;
    if (under) {
      nodes.push(inlineBlock('heading', line.trim(), f, { level: under[1][0] === '=' ? 1 : 2 }));
      i += 2;
      continue;
    }

    nodes.push(inlineBlock('paragraph', line, f));
    i++;
  }

  return nodes;
};

export const markdownToTiptapNodes = (markdown: string, features: MarkdownFeatures = {}): TiptapNode[] =>
  parseBlocks(markdown.replace(/\\n/g, '\n').split('\n'), features);

// ── Serialize ───────────────────────────────────────────────────────────────────────────────────

/** Wrap one inline atom — a text run or an image — in its marks, innermost delimiter first. A code
 *  span's backticks sit inside every other wrapper, and the link wrapper goes outermost so both
 *  `[**bold**](href)` and `[![alt](img)](href)` re-parse to the marks they were built from. */
const applyMarks = (core: string, marks: TiptapMark[] | undefined): string => {
  const has = (type: string) => marks?.some(m => m.type === type);
  let text = core;
  if (has('code')) text = `\`${text}\``;
  if (has('bold')) text = `**${text}**`;
  if (has('italic')) text = `*${text}*`;
  if (has('strike')) text = `~~${text}~~`;
  const href = marks?.find(m => m.type === 'link')?.attrs?.href;
  if (typeof href === 'string' && href) text = `[${text}](${href})`;
  return text;
};

const serializeInlineContent = (nodes: TiptapNode[] | undefined): string => {
  if (!nodes) return '';
  return nodes
    .map(node => {
      if (node.type === 'image') {
        const { src, alt, title } = (node.attrs ?? {}) as { src?: string; alt?: string; title?: string };
        return src ? applyMarks(`![${alt ?? ''}](${src}${title ? ` "${title}"` : ''})`, node.marks) : '';
      }
      if (node.type === 'hardBreak') return ' ';
      if (node.type !== 'text' || !node.text) return '';
      return applyMarks(node.text, node.marks);
    })
    .join('');
};

const NESTED_INDENT = '  ';
const indentLines = (block: string, indent: string): string =>
  block.split('\n').map(l => (l ? indent + l : l)).join('\n');

const LIST_TYPES = new Set(['bulletList', 'orderedList', 'taskList']);

const serializeList = (node: TiptapNode): string => {
  const ordered = node.type === 'orderedList';
  const task = node.type === 'taskList';
  const lines: string[] = [];

  (node.content ?? []).forEach((item, idx) => {
    const children = item.content ?? [];
    const paragraphs = children.filter(c => c.type === 'paragraph');
    const box = task ? (item.attrs?.checked ? '[x] ' : '[ ] ') : '';
    lines.push(`${ordered ? `${idx + 1}. ` : '- '}${box}${serializeInlineContent(paragraphs[0]?.content)}`);

    for (const extra of paragraphs.slice(1)) {
      const text = serializeInlineContent(extra.content);
      if (text) lines.push(indentLines(text, NESTED_INDENT));
    }
    for (const sub of children.filter(c => LIST_TYPES.has(c.type))) {
      lines.push(indentLines(serializeList(sub), NESTED_INDENT));
    }
  });

  return lines.join('\n');
};

export const tiptapNodesToMarkdown = (nodes: TiptapNode[]): string => {
  const parts: string[] = [];

  for (const node of nodes) {
    switch (node.type) {
      case 'heading': {
        const level = (node.attrs?.level as number) || 4;
        parts.push(`${'#'.repeat(level)} ${serializeInlineContent(node.content)}`);
        break;
      }
      case 'bulletList':
      case 'orderedList':
      case 'taskList': {
        const list = serializeList(node);
        if (list) parts.push(list);
        break;
      }
      case 'blockquote': {
        const inner = tiptapNodesToMarkdown(node.content ?? []);
        parts.push(inner ? inner.split('\n').map(l => (l ? `> ${l}` : '>')).join('\n') : '>');
        break;
      }
      case 'codeBlock': {
        const text = (node.content ?? []).map(c => c.text ?? '').join('');
        const language = typeof node.attrs?.language === 'string' ? node.attrs.language : '';
        parts.push(`\`\`\`${language}\n${text}\n\`\`\``);
        break;
      }
      case 'horizontalRule': {
        parts.push('---');
        break;
      }
      case 'paragraph': {
        parts.push(serializeInlineContent(node.content));
        break;
      }
      default:
        break;
    }
  }

  return parts.join('\n\n');
};

export const convertCanvasContentToMarkdown = (content: unknown): string => {
  if (typeof content === 'string') {
    return content;
  }
  if (!content || typeof content !== 'object') {
    return '';
  }
  const doc = content as TiptapDoc;
  if (doc.type === 'doc' && Array.isArray(doc.content)) {
    return tiptapNodesToMarkdown(doc.content);
  }
  return '';
};

export const stripDiffAttributes = (nodes: TiptapNode[]): TiptapNode[] => {
  return nodes.map(node => {
    const newNode = { ...node };
    
    if (newNode.attrs) {
      const { diffType, ...restAttrs } = newNode.attrs as Record<string, unknown> & { diffType?: unknown };
      newNode.attrs = Object.keys(restAttrs).length > 0 ? restAttrs : undefined;
    }
    
    if (newNode.content) {
      newNode.content = stripDiffAttributes(newNode.content);
    }
    
    return newNode;
  });
};

const unwrapContent = (content: string): string => {
  if (content.startsWith('"') && content.endsWith('"')) {
    try {
      const unwrapped = JSON.parse(content);
      if (typeof unwrapped === 'string') return unwrapped;
    } catch {}
  }
  return content;
};

const parseContentToNodes = (content: string | null): TiptapNode[] | null => {
  if (!content) return null;
  const doc = parseTiptapJson(content);
  if (doc) return doc.content || [];
  const unwrapped = unwrapContent(content);
  if (isMarkdownContent(unwrapped)) return markdownToTiptapNodes(unwrapped);
  return null;
};

const buildSectionNodes = (
  section: PlanningSection | undefined,
  defaultText: string
): TiptapNode[] => {
  const currentContent = section?.content || null;
  const pendingContent = section?.pendingContent || null;
  const hasPending = !!pendingContent;

  const currentNodes = parseContentToNodes(currentContent);
  const pendingNodes = parseContentToNodes(pendingContent);

  if (hasPending && pendingNodes) {
    const cleanCurrent = stripDiffAttributes(currentNodes || []);
    const cleanPending = stripDiffAttributes(pendingNodes);
    return computeDiffContent(cleanCurrent, cleanPending);
  } else if (currentNodes) {
    return stripDiffAttributes(currentNodes);
  } else {
    return [{ type: 'paragraph', content: [{ type: 'text', text: defaultText }] }];
  }
};

export const buildDocContent = (
  goalSection: PlanningSection | undefined,
  outlineSection: PlanningSection | undefined,
  showGoalOnly?: boolean,
  showOutlineOnly?: boolean
): TiptapDoc => {
  const goalNodes = buildSectionNodes(goalSection, 'Start planning your story goal...');
  const outlineNodes = buildSectionNodes(outlineSection, 'Outline will be generated here...');

  if (showGoalOnly) {
    return {
      type: 'doc',
      content: [
        {
          type: 'collapsibleSection',
          attrs: { sectionType: 'goal', title: 'Goal', isExpanded: true },
          content: goalNodes,
        },
      ],
    };
  }

  if (showOutlineOnly) {
    return {
      type: 'doc',
      content: [
        {
          type: 'collapsibleSection',
          attrs: { sectionType: 'outline', title: 'Outline', isExpanded: true },
          content: outlineNodes,
        },
      ],
    };
  }

  return {
    type: 'doc',
    content: [
      {
        type: 'collapsibleSection',
        attrs: { sectionType: 'goal', title: 'Goal', isExpanded: true },
        content: goalNodes,
      },
      {
        type: 'collapsibleSection',
        attrs: { sectionType: 'outline', title: 'Outline', isExpanded: !!outlineSection?.content || !!outlineSection?.pendingContent },
        content: outlineNodes,
      },
    ],
  };
};

export const extractSectionContent = (doc: TiptapDoc, sectionType: string): TiptapNode[] => {
  if (!doc.content) return [];
  const section = doc.content.find(
    (node) => node.type === 'collapsibleSection' && node.attrs?.sectionType === sectionType
  );
  return section?.content || [];
};
